/**
 * Canonical binary serialization. Fixed field order (writers control order),
 * little-endian, length-prefixed strings and buffers. No JSON, no object key
 * enumeration - byte-identical output on every engine for identical state.
 * Used for snapshots, the state hash, and save payloads.
 */

/** Bounds-checked read (noUncheckedIndexedAccess) without non-null assertions. */
function at(data: Uint8Array, i: number): number {
  const v = data[i];
  if (v === undefined) throw new Error(`serialize: index ${String(i)} out of range`);
  return v;
}

export class BinWriter {
  private buf: number[] = [];
  private scratch = new DataView(new ArrayBuffer(8));

  u8(v: number): this { this.buf.push(v & 0xff); return this; }

  u16(v: number): this {
    this.scratch.setUint16(0, v & 0xffff, true);
    for (let i = 0; i < 2; i++) this.buf.push(this.scratch.getUint8(i));
    return this;
  }

  u32(v: number): this {
    this.scratch.setUint32(0, v >>> 0, true);
    for (let i = 0; i < 4; i++) this.buf.push(this.scratch.getUint8(i));
    return this;
  }

  i32(v: number): this {
    this.scratch.setInt32(0, v | 0, true);
    for (let i = 0; i < 4; i++) this.buf.push(this.scratch.getUint8(i));
    return this;
  }

  /** Raw IEEE-754 bits - exact round trip, no arithmetic. */
  f64(v: number): this {
    this.scratch.setFloat64(0, v, true);
    for (let i = 0; i < 8; i++) this.buf.push(this.scratch.getUint8(i));
    return this;
  }

  /** Signed 64-bit integer (BigInt intermediate; value must be an integer). */
  i64(v: number): this {
    this.scratch.setBigInt64(0, BigInt(v), true);
    for (let i = 0; i < 8; i++) this.buf.push(this.scratch.getUint8(i));
    return this;
  }

  u64(v: number): this {
    this.scratch.setBigUint64(0, BigInt(v), true);
    for (let i = 0; i < 8; i++) this.buf.push(this.scratch.getUint8(i));
    return this;
  }

  bool(v: boolean): this { return this.u8(v ? 1 : 0); }

  str(s: string): this {
    const bytes = utf8Encode(s);
    this.u32(bytes.length);
    for (const b of bytes) this.buf.push(b);
    return this;
  }

  bytes(b: Uint8Array): this {
    this.u32(b.length);
    for (const byte of b) this.buf.push(byte);
    return this;
  }

  finish(): Uint8Array { return Uint8Array.from(this.buf); }
}

export class BinReader {
  private pos = 0;
  private view: DataView;
  constructor(private readonly data: Uint8Array) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }

  u8(): number {
    const v = this.view.getUint8(this.pos); this.pos += 1; return v;
  }
  u16(): number {
    const v = this.view.getUint16(this.pos, true); this.pos += 2; return v;
  }
  u32(): number {
    const v = this.view.getUint32(this.pos, true); this.pos += 4; return v;
  }
  i32(): number {
    const v = this.view.getInt32(this.pos, true); this.pos += 4; return v;
  }
  f64(): number {
    const v = this.view.getFloat64(this.pos, true); this.pos += 8; return v;
  }
  i64(): number {
    const v = this.view.getBigInt64(this.pos, true); this.pos += 8; return Number(v);
  }
  u64(): number {
    const v = this.view.getBigUint64(this.pos, true); this.pos += 8; return Number(v);
  }
  bool(): boolean { return this.u8() !== 0; }
  /** Read the u32 at the current position without consuming it. Lets a
   *  deserializer detect an optional magic+version header. */
  peekU32(): number {
    return this.view.getUint32(this.pos, true);
  }
  str(): string {
    const len = this.u32();
    const bytes = this.data.subarray(this.pos, this.pos + len);
    this.pos += len;
    return utf8Decode(bytes);
  }
  bytes(): Uint8Array {
    const len = this.u32();
    return this.rawBytes(len);
  }
  /** Read exactly len bytes with no length prefix. */
  rawBytes(len: number): Uint8Array {
    const out = this.data.slice(this.pos, this.pos + len);
    this.pos += len;
    return out;
  }
  /** Isolated parsing of the remaining bounded system blob. */
  fork(): BinReader { return new BinReader(this.data.subarray(this.pos)); }
  get remaining(): number { return this.data.length-this.pos; }
  get done(): boolean { return this.pos >= this.data.length; }
}

function utf8Encode(s: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    let cp = s.charCodeAt(i);
    if (cp >= 0xd800 && cp <= 0xdbff && i + 1 < s.length) {
      const lo = s.charCodeAt(i + 1);
      if (lo >= 0xdc00 && lo <= 0xdfff) {
        cp = 0x10000 + ((cp - 0xd800) << 10) + (lo - 0xdc00);
        i++;
      }
    }
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
  }
  return Uint8Array.from(out);
}

function utf8Decode(bytes: Uint8Array): string {
  let s = '';
  let i = 0;
  while (i < bytes.length) {
    const b0 = at(bytes, i);
    let cp: number;
    if (b0 < 0x80) { cp = b0; i += 1; }
    else if (b0 < 0xe0) { cp = ((b0 & 0x1f) << 6) | (at(bytes, i + 1) & 0x3f); i += 2; }
    else if (b0 < 0xf0) { cp = ((b0 & 0x0f) << 12) | ((at(bytes, i + 1) & 0x3f) << 6) | (at(bytes, i + 2) & 0x3f); i += 3; }
    else { cp = ((b0 & 0x07) << 18) | ((at(bytes, i + 1) & 0x3f) << 12) | ((at(bytes, i + 2) & 0x3f) << 6) | (at(bytes, i + 3) & 0x3f); i += 4; }
    s += String.fromCodePoint(cp);
  }
  return s;
}
